import { sanitizeTelemetryText, sanitizeTelemetryValue } from '../integrations/gmail/gmailAttemptVault';

const ERROR_API_URL = 'https://api.varannanvecka.app:443/v1/error/';
export const MAX_REPORTS_PER_SESSION = 3;
export const RECENT_REPORT_WINDOW_MS = 1500;
export const ERROR_REPORT_TIMEOUT_MS = 5000;

export interface ICrashlyticsStackFrame {
  functionName: string;
  fileName: string;
  lineNumber: number;
  columnNumber?: number;
  source?: string;
}

interface IReportOptions {
  fingerprint?: string;
  stackFrames?: ICrashlyticsStackFrame[];
}

interface IQueuedReport {
  errorText: string;
  fingerprint: string;
}

/**
 * One reporter per app session, shared by global errors and React boundaries.
 * The session cap includes queued and in-flight reports, bounding memory and
 * requests. Native reporting happens on acceptance, independently of the POST.
 */
export function createErrorReporter(
  version: number,
  reportToNative: (errorText: string, frames: ICrashlyticsStackFrame[]) => void
): (errorText: string, options?: IReportOptions) => void {
  const queue: IQueuedReport[] = [];
  const pendingFingerprints = new Set<string>();
  const recentReports = new Map<string, number>();
  let acceptedCount = 0;
  let capturing = false;
  let sending = false;

  const sendNext = (): void => {
    if (sending || queue.length === 0) {
      return;
    }
    const report = queue.shift()!;
    sending = true;
    let finished = false;
    let controller: AbortController | undefined;

    const finish = () => {
      if (finished) {
        return;
      }
      finished = true;
      clearTimeout(timeout);
      pendingFingerprints.delete(report.fingerprint);
      sending = false;
      sendNext();
    };
    const timeout = setTimeout(() => {
      if (finished) {
        return;
      }
      // Abort when supported, but also advance on older WebViews without it.
      // The finished guard prevents late responses from disturbing the queue.
      try {
        if (controller) {
          controller.abort();
        }
      } catch {
        // An unavailable native abort must not leave reporting stuck.
      }
      console.warn('Error report request timed out.');
      finish();
    }, ERROR_REPORT_TIMEOUT_MS);

    try {
      controller = typeof AbortController === 'function' ? new AbortController() : undefined;
      const request: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ error: report.errorText, version }),
        keepalive: true,
      };
      if (controller) {
        request.signal = controller.signal;
      }
      // Consume both synchronous failures and rejected promises locally;
      // reporting failures must not create unhandledrejection reports.
      void Promise.resolve(fetch(ERROR_API_URL, request)).then((response) => {
        if (finished) {
          return;
        }
        if (!response.ok) {
          console.warn(`Error report rejected by backend (HTTP ${response.status}).`);
        }
        finish();
      }, () => {
        if (!finished) {
          console.warn('Failed to send error report to backend.');
          finish();
        }
      });
    } catch {
      console.warn('Could not start error report request.');
      finish();
    }
  };

  return (errorText, options = {}) => {
    // Only synchronous re-entry from the reporting code is suppressed. A
    // different application error during an asynchronous POST is accepted.
    if (capturing || acceptedCount >= MAX_REPORTS_PER_SESSION) {
      return;
    }
    capturing = true;
    try {
      const safeErrorText = sanitizeTelemetryText(errorText);
      const fingerprint = sanitizeTelemetryText(options.fingerprint || safeErrorText);
      const now = Date.now();
      const lastReportedAt = recentReports.get(fingerprint);
      if (pendingFingerprints.has(fingerprint) ||
          (lastReportedAt !== undefined && now - lastReportedAt < RECENT_REPORT_WINDOW_MS)) {
        return;
      }

      acceptedCount += 1;
      recentReports.set(fingerprint, now);
      pendingFingerprints.add(fingerprint);
      queue.push({ errorText: safeErrorText, fingerprint });

      try {
        const frames = sanitizeTelemetryValue(options.stackFrames || []) as ICrashlyticsStackFrame[];
        reportToNative(safeErrorText, frames);
      } catch {
        console.warn('Failed to forward error to native crash reporting.');
      }
    } finally {
      capturing = false;
    }
    sendNext();
  };
}



// WEBPACK FOOTER //
// ./src/helpers/errorReporter.ts